import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import DeleteConfirmModal from "./delete-confirm-modal";
import {
  adminCourseGetAction,
  adminCourseOnboardingStepDeleteAction,
  adminCourseOnboardingStepsListAction
} from "../Action/action";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

export default function CourseOnboardingSteps() {
  const { courseId } = useParams();
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [reloadNonce, setReloadNonce] = useState(0);

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [steps, setSteps] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 10 });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadCourse() {
      try {
        const res = await adminCourseGetAction(courseId);
        if (cancelled) return;
        if (res?.success) setCourse(res.data || null);
      } catch (e) {}
    }
    if (courseId) loadCourse();
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  useEffect(() => {
    setPage(1);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    const q = query.trim() ? query.trim() : undefined;

    const t = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const res = await adminCourseOnboardingStepsListAction(courseId, { page, per_page: perPage, q });
        if (cancelled) return;
        if (!res?.success) throw new Error(res?.message || "Failed to load onboarding steps.");
        setSteps(Array.isArray(res.data) ? res.data : []);
        setMeta({
          total: Number(res?.meta?.total || 0),
          page: Number(res?.meta?.page || page),
          per_page: Number(res?.meta?.per_page || perPage)
        });
      } catch (e) {
        if (cancelled) return;
        setSteps([]);
        setMeta({ total: 0, page, per_page: perPage });
        setError(e?.response?.data?.message || e?.message || "Failed to load onboarding steps.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [courseId, page, perPage, query, reloadNonce]);

  const rows = useMemo(() => {
    return steps.map((s) => ({
      raw: s,
      id: s?.id,
      stepNumber: s?.step_number ?? "—",
      title: s?.title || "—",
      subtitle: s?.subtitle || "—",
      stepType:
        s?.step_type === "form"
          ? "Form"
          : s?.step_type === "video"
            ? "Video"
            : s?.step_type === "question"
              ? "Question"
              : s?.step_type === "notification"
                ? "Notification"
                : s?.step_type || "—",
      created: formatDate(s?.created_at)
    }));
  }, [steps]);

  const total = meta?.total ? Number(meta.total) : rows.length;
  const totalPages = total ? Math.max(1, Math.ceil(total / perPage)) : 1;
  const start = total && rows.length ? (page - 1) * perPage + 1 : 0;
  const end = total && rows.length ? start + rows.length - 1 : 0;

  const pageButtons = useMemo(() => {
    const max = 5;
    const safeTotal = Math.max(1, totalPages);
    const current = Math.min(Math.max(1, page), safeTotal);
    let s = Math.max(1, current - 2);
    let e = Math.min(safeTotal, s + max - 1);
    s = Math.max(1, e - max + 1);
    const arr = [];
    for (let i = s; i <= e; i += 1) arr.push(i);
    return arr;
  }, [page, totalPages]);

  const clearAndReload = () => {
    setQuery("");
    setPage(1);
    setError("");
    setReloadNonce((n) => n + 1);
  };

  const openDelete = (row) => {
    if (!row?.id) return;
    setDeleteRow(row);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);
    try {
      const res = await adminCourseOnboardingStepDeleteAction(deleteRow.id);
      if (!res?.success) throw new Error(res?.message || "Delete failed");
      toast.success("Step deleted", { toastId: "step-delete" });
      setDeleteOpen(false);
      setDeleteRow(null);
      setReloadNonce((n) => n + 1);
    } catch (e) {
      toast.error("Delete failed", { toastId: "step-delete" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Onboarding steps unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="breadcrumb">
        <Link to="/courses">Courses</Link>
        <span>›</span>
        <span>Onboarding Steps</span>
      </div>

      <div className="toolbar">
        <div className="flex items-center gap-3">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search steps..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <button className="btn btn-outline" type="button" onClick={clearAndReload} disabled={loading}>
            ↻ Clear
          </button>
        </div>
        
        <div className="flex items-center gap-3">
          <Link to="/courses" className="btn btn-outline btn-sm">
            ← Back
          </Link>
          
          <Link to="new" className="btn btn-primary">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
            Add New Step
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Title</th>
                <th>Subtitle</th>
                <th>Type</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7}>
                    <div className="empty-state" style={{ padding: "28px 12px" }}>
                      Loading…
                    </div>
                  </td>
                </tr>
              ) : rows.map((r) => (
                <tr key={r.id}>
                  <td>{start + rows.indexOf(r)}</td>
                  <td><strong>{r.title}</strong></td>
                  <td>{r.subtitle}</td>
                  <td>{r.stepType}</td>
                  <td>{r.created}</td>
                  <td>
                    <div className="actions-cell">
                      <Link to={`${r.id}/edit`} className="action-link">Edit</Link>
                      <button
                        type="button"
                        className="action-link text-danger"
                        onClick={() => openDelete(r)}
                        style={{ background: "transparent", border: "none", padding: 0 }}
                      >
                        Delete
                      </button>
                  
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="empty-state">
                      <div className="empty-icon">🫥</div>
                      No onboarding steps found.
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <span>
            Showing {start}-{end} of {total} Steps
          </span>
          <div className="pagination-pages">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page <= 1 || loading}
              title="First page"
              aria-label="First page"
            >
              {"‹‹"}
            </button>
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>‹</button>
            {pageButtons.map((p) => (
              <button key={p} type="button" className={p === page ? "active" : ""} onClick={() => setPage(p)} disabled={loading}>
                {p}
              </button>
            ))}
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading}>›</button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages || loading}
              title="Last page"
              aria-label="Last page"
            >
              {"››"}
            </button>
          </div>
        </div>
      </div>

      <DeleteConfirmModal
        open={deleteOpen}
        itemType="onboarding step"
        itemName={deleteRow?.title || "Onboarding Step"}
        onCancel={() => {
          if (deleting) return;
          setDeleteOpen(false);
          setDeleteRow(null);
        }}
        onConfirm={confirmDelete}
        confirmDisabled={deleting}
      />
    </div>
  );
}
